"use client";
import { motion } from "framer-motion";

/* Orbiting dot */
function OrbitDot({ radius, duration, delay, size = 3, color = "#00f0ff" }) {
  return (
    <motion.div
      className="absolute top-1/2 left-1/2 rounded-full"
      style={{
        width: size,
        height: size,
        background: color,
        marginLeft: -size / 2,
        marginTop: -size / 2,
      }}
      animate={{
        x: [
          radius,
          radius * 0.707,
          0,
          -radius * 0.707,
          -radius,
          -radius * 0.707,
          0,
          radius * 0.707,
          radius,
        ],
        y: [
          0,
          -radius * 0.707,
          -radius,
          -radius * 0.707,
          0,
          radius * 0.707,
          radius,
          radius * 0.707,
          0,
        ],
        opacity: [0.8, 1, 0.8, 1, 0.8, 1, 0.8, 1, 0.8],
      }}
      transition={{ duration, delay, repeat: Infinity, ease: "linear" }}
    />
  );
}

/* Scanning bar */
function ScanLine() {
  return (
    <motion.div
      className="absolute left-0 right-0 h-px pointer-events-none"
      style={{
        background:
          "linear-gradient(90deg, transparent, #00f0ff60, #00f0ff, #00f0ff60, transparent)",
      }}
      animate={{ top: ["0%", "100%", "0%"] }}
      transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
    />
  );
}

export default function Loading() {
  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden"
      style={{ background: "#05050b" }}
    >
      {/* ── Grid bg ── */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.03]"
        aria-hidden
      >
        <defs>
          <pattern
            id="load-grid"
            width="72"
            height="72"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 72 0 L 0 0 0 72"
              fill="none"
              stroke="white"
              strokeWidth="0.4"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#load-grid)" />
      </svg>

      {/* ── Ambient orbs ── */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <motion.div
          className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(0,71,255,0.15) 0%, transparent 70%)",
            filter: "blur(80px)",
          }}
          animate={{ scale: [1, 1.15, 1], opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(0,240,255,0.1) 0%, transparent 70%)",
            filter: "blur(80px)",
          }}
          animate={{ scale: [1, 1.2, 1], opacity: [0.4, 0.9, 0.4] }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
        />
      </div>

      {/* ── Scan window ── */}
      <div className="relative w-64 h-64 mb-10">
        {/* Corner brackets */}
        {[
          { top: 0, left: 0, path: "M0 32 L0 0 L32 0" },
          { top: 0, right: 0, path: "M0 0 L32 0 L32 32" },
          { bottom: 0, left: 0, path: "M0 0 L0 32 L32 32" },
          { bottom: 0, right: 0, path: "M32 0 L32 32 L0 32" },
        ].map((corner, i) => (
          <motion.svg
            key={i}
            className="absolute"
            style={{
              top: corner.top,
              right: corner.right,
              bottom: corner.bottom,
              left: corner.left,
            }}
            width="32"
            height="32"
            fill="none"
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
              delay: i * 0.1,
              duration: 0.5,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            <path
              d={corner.path}
              stroke="#00f0ff"
              strokeWidth="1.5"
              opacity="0.6"
            />
          </motion.svg>
        ))}

        {/* Scan line inside the window */}
        <div className="absolute inset-4 overflow-hidden">
          <ScanLine />
        </div>

        {/* Center logo */}
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative flex items-center justify-center"
          >
            {/* Pulsing outer ring */}
            <motion.div
              className="absolute rounded-full border"
              style={{
                borderColor: "rgba(0,240,255,0.15)",
                width: 100,
                height: 100,
              }}
              animate={{ scale: [1, 1.3, 1], opacity: [0.4, 0, 0.4] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            {/* Main ring */}
            <motion.div
              className="absolute rounded-full border"
              style={{
                borderColor: "rgba(0,240,255,0.4)",
                width: 72,
                height: 72,
              }}
              animate={{ rotate: 360 }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            />
            {/* Dashed inner ring */}
            <motion.div
              className="absolute rounded-full"
              style={{
                width: 54,
                height: 54,
                border: "1px dashed rgba(0,71,255,0.4)",
              }}
              animate={{ rotate: -360 }}
              transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
            />

            {/* Orbiting dots */}
            <div
              className="absolute w-full h-full"
              style={{ width: 72, height: 72 }}
            >
              <OrbitDot
                radius={36}
                duration={3}
                delay={0}
                size={4}
                color="#00f0ff"
              />
              <OrbitDot
                radius={36}
                duration={3}
                delay={1.5}
                size={3}
                color="#0047ff"
              />
              <OrbitDot
                radius={54}
                duration={4.5}
                delay={0.75}
                size={2.5}
                color="rgba(0,240,255,0.5)"
              />
            </div>

            {/* Logo text */}
            <motion.span
              className="relative z-10 text-base font-black"
              style={{
                fontFamily: "'Syne', sans-serif",
                background:
                  "linear-gradient(135deg, #ffffff, rgba(255,255,255,0.6))",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
              animate={{ opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              AB
            </motion.span>
          </motion.div>
        </div>
      </div>

      {/* ── Progress bar ── */}
      <div
        className="relative w-48 h-px mb-6 overflow-hidden"
        style={{ background: "rgba(255,255,255,0.05)" }}
      >
        <motion.div
          className="absolute top-0 left-0 h-full"
          style={{ background: "linear-gradient(90deg, #0047ff, #00f0ff)" }}
          animate={{ x: ["-100%", "100%"] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* ── Status text ── */}
      <motion.p
        className="text-[10px] tracking-[0.35em] uppercase text-white/30"
        style={{ fontFamily: "'DM Mono', monospace" }}
        animate={{ opacity: [0.3, 0.7, 0.3] }}
        transition={{ duration: 1.8, repeat: Infinity }}
      >
        Initializing…
      </motion.p>

      {/* ── Bottom stamp ── */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        <span
          className="text-[9px] tracking-[0.28em] uppercase text-white/14"
          style={{ fontFamily: "'DM Mono', monospace" }}
        >
          Abu Bokor — Portfolio
        </span>
      </motion.div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@800;900&family=DM+Mono:wght@400&display=swap');
      `}</style>
    </div>
  );
}
